/**
 * Who counts as a buyer-only login.
 *
 * External buyers hold real app logins so they can open the buyer map,
 * but they are on the other side of the table from the people we buy
 * from. Anything that is the buy side's working material — the Lead
 * Center roster of owner names, phone numbers and what we are willing
 * to pay — is closed to them.
 *
 * This lives on its own, with no environment access and no framework
 * imports, so the browser rule (src/utils/accessControl.js) and the
 * serverless rule (api/_lib/auth.js) are the same rule rather than two
 * copies of a list that drift apart. Both are given an object carrying
 * `role` and `permissions`: the client passes the signed-in user, the
 * server passes the public.profiles row, and those two agree on shape.
 */

// Spellings seen across invites, seed data and hand-edited profiles.
// Matching is on the normalized token, so case and padding never open
// a door that the exact spelling would close.
export const BUYER_ONLY_ROLES = new Set([
  'buyer',
  'buyer-only',
  'buyer_only',
  'external-buyer',
  'external_buyer',
]);

export const BUYER_ONLY_PERMISSIONS = new Set([
  'buyer-map-only',
  'buyer_map_only',
  'buyer-only',
  'buyer_only',
  'external-buyer',
  'external_buyer',
]);

export function normalizeAccessToken(value) {
  return String(value || '').trim().toLowerCase();
}

export function isBuyerOnlyProfile(profile) {
  if (!profile) return false;

  if (BUYER_ONLY_ROLES.has(normalizeAccessToken(profile.role))) return true;

  const permissions = Array.isArray(profile.permissions) ? profile.permissions : [];
  return permissions.some((permission) => BUYER_ONLY_PERMISSIONS.has(normalizeAccessToken(permission)));
}
